import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { MarketsService } from '@src/app/shared/backend/clients';
import { BaseListComponent } from '@src/app/shared/components/base-list/base-list.component';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { TableSettingService } from '@src/app/shared/services/table.setting.service';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FiltersPanelChangeEvent } from '@src/app/shared/components/filters-panel/filters-panel.models';
import { applyFiltersPanelChange } from '@src/app/shared/components/filters-panel/filters-panel.helper';
import { loadStoredPaginatorState, resetPaginatorStateToFirstPage } from '@app/shared/components/paginator/paginator-storage.helper';

@Component({
    selector: 'app-markets',
    templateUrl: './markets.component.html',
    styleUrl: './markets.component.scss',
    host: {
        class: 'app-markets'
    },
    standalone: false
})
export class MarketsComponent extends BaseListComponent implements OnInit, OnDestroy {

  private initialLoadDone = false;
  private readonly componentDestroyed$ = new Subject<void>();

  public readonly marketsTableName = TableSettingService.MarketsTableName;

  allowNewMarket: boolean = false;

  constructor(
    private router: Router,
    public override tableSettingService: TableSettingService,
    private marketsService: MarketsService,
    private permissionService: PermissionService,
    private translateService: TranslateService
  ) {
    super();
  }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.restorePaginatorStateFromStorage();

    this.translateService.onLangChange
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.rebuildSortPanelColumnLabels();
      });

    await this.tableSettingService.init();
    await super.setCache(this.marketsTableName);

    this.initSortPanel({
      defaultSort: { key: 'code', direction: 'ASC' },
      labelResolver: (col) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        if (translated && translated !== translationKey) {
          return translated;
        }

        return col.header;
      }
    });

    this.allowNewMarket = await this.permissionService.hasActionPermissions(Actions.create_markets);

    this.rebuildSortPanelColumnLabels();

    // Fallback: if the panel doesn't restore anything, load unfiltered!!
    setTimeout(async () => {
      if (!this.initialLoadDone) {
        this.initialLoadDone = true;
        await this.get();
      }
    });
  }

  override async get() {
    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    this.applySortToFilterRequest();

    this.page = await firstValueFrom(this.marketsService.apiMarketsFilterTablePost(this.filter));
    this.data = this.page.results;
  }

  override onPageChange(event: any): void {
    this.initialLoadDone = true;
    super.onPageChange(event);
  }

  protected override onCriteriaResetRequested(): void {
    resetPaginatorStateToFirstPage({
      tableName: this.marketsTableName,
      pageSize: this.pageSize,
    });
  }

  private restorePaginatorStateFromStorage(): void {
    const stored = loadStoredPaginatorState(this.marketsTableName);

    if (!stored) {
      return;
    }

    const storedPageNumber = stored.valuesByFieldKey.pageNumber;
    const storedPageSize = stored.valuesByFieldKey.pageSize;

    this.pageNumber = storedPageNumber;
    this.pageSize = storedPageSize;

    this.filter.pageNumber = storedPageNumber;
    this.filter.pageSize = storedPageSize;
  }

  async onFiltersPanelChange(evt: FiltersPanelChangeEvent): Promise<void> {
    const currentFilters = this.filter.filters as any;

    const nextFilters = applyFiltersPanelChange(
      evt,
      currentFilters,
      this.allTableColumns
    );

    if (!nextFilters) {
      return;
    }

    this.filter.filters = nextFilters as any;

    const isApplyAction = evt.source === 'apply';
    const isClearAction = evt.source === 'clear';
    const shouldResetPaging = isApplyAction || isClearAction;

    if (shouldResetPaging) {
      const firstPageNumber = 1;

      this.pageNumber = firstPageNumber;
      this.filter.pageNumber = firstPageNumber;

      this.onCriteriaResetRequested();
    }

    this.initialLoadDone = true;
    await this.get();
  }

  private rebuildSortPanelColumnLabels(): void {
    const visibleColumns = Array.isArray(this.tableColumns) ? this.tableColumns : [];

    const nextSortableColumns = visibleColumns
      .filter((col: any) => {
        const field = String(col?.field ?? '').trim();
        return (
          field.length > 0 &&
          field.toLowerCase() !== 'actions' &&
          col?.sortable !== false
        );
      })
      .map((col: any) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        return {
          key: col.field,
          label: translated && translated !== translationKey ? translated : col.header
        };
      });

    this.sortableColumns = nextSortableColumns;

    // clear active sort if points to col that is not available due to system settings update
    const allowedKeys = new Set(nextSortableColumns.map(col => col.key));
    if (this.sortPanelSort?.key && !allowedKeys.has(this.sortPanelSort.key)) {
      this.onSortPanelChange(null);
    }
  }

  goToCreateMarketForm(): void {
    this.router.navigate(['/markets/add']);
  }

  goToMarketDetails(event: any, marketId: string) {
    this.router.navigate([`/markets/${marketId}`]);
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
